import { useEffect, useMemo, useRef, useState } from "react";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import NoTableEntries from "@components/DataTable/NoTableEntries";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import DisputeDetailsDrawer from "@features/vessel-call/vessel-call-status/Operations/drawers/DisputeDetailsDrawer";
import DisputeDrawer from "@features/vessel-call/vessel-call-status/Operations/drawers/DisputeDrawer";
import ResolveDisputeDrawer from "@features/vessel-call/vessel-call-status/Operations/drawers/ResolveDisputeDrawer";
import ReviewResolutionDrawer, {
  type ReviewResolutionMode,
} from "@features/vessel-call/vessel-call-status/Operations/drawers/ReviewResolutionDrawer";
import OperationsDisputesFlowButtons from "@features/vessel-call/vessel-call-status/Operations/flow/OperationsDisputesFlowButtons";
import { mapDisputeToRow } from "@features/vessel-call/vessel-call-status/Operations/grid/OperationsDisputes.mapper";
import {
  buildDisputeFilterPayload,
  getDisputeFilterFields,
} from "@features/vessel-call/vessel-call-status/Operations/grid/utils";
import type { DisputeRow } from "@features/vessel-call/vessel-call-status/Operations/types/types";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { getDisputes } from "@services/disputesApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { FromToLocation } from "@services/vessel-call/types";
import type { VesselCallResponse } from "@services/vesselCall/types";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { useTranslation } from "react-i18next";
import { useOutletContext, useParams } from "react-router-dom";

const LANG_BASE = "vessel-call.operations";
const SERVICE = "bff-vessel-call";
const LOCAL_STORAGE_FILTERS_KEY = "operations-disputes-filters";

const LOCATION_FOAA = "FOAA";
const SEARCH_INQUIRY_ID = "vessel-call.operations.disputes.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Operations",
  action: "search_operations_disputes",
  entityType: "dispute",
};

const OperationsDisputes = () => {
  const { t } = useTranslation();
  const { id: vesselCallId = "" } = useParams();

  const { vesselCallDetails } = useOutletContext<{
    vesselCallDetails: VesselCallResponse;
  }>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedDisputeId, setSelectedDisputeId] = useState<string | null>(null);
  const [isDisputeDetailsDrawerOpen, setIsDisputeDetailsDrawerOpen] = useState(false);
  const [isNoDisputeDrawerOpen, setIsNoDisputeDrawerOpen] = useState(false);
  const [isDisputeDrawerOpen, setIsDisputeDrawerOpen] = useState(false);
  const [isUpdateDisputeDrawerOpen, setIsUpdateDisputeDrawerOpen] = useState(false);
  const [isResolveDisputeDrawerOpen, setIsResolveDisputeDrawerOpen] = useState(false);
  const [reviewResolutionMode, setReviewResolutionMode] = useState<ReviewResolutionMode | null>(
    null,
  );

  //Dispute can be Resolved only if the vessel is in FOAA location
  const isCurrentLocationFOAA = useMemo(() => {
    return vesselCallDetails?.currentLocation === LOCATION_FOAA;
  }, [vesselCallDetails?.currentLocation]);

  const { options: fromToLocations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    SERVICE,
  );

  const {
    data: disputesData,
    isLoading,
    isError,
    isFetching,
    isSuccess,
    dataUpdatedAt,
    error,
  } = useQuery({
    queryKey: ["disputes", SERVICE, vesselCallId, page, pageSize, filters],
    queryFn: () =>
      getDisputes({
        service: SERVICE,
        payload: buildDisputeFilterPayload(vesselCallId, filters),
        page: page - 1,
        pageSize,
      }),
    enabled: Boolean(vesselCallId),
  });

  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (!vesselCallId || isLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Operations");
    trackScreenLoad("Vessel Call Management", "Operations", startedAt.current);
  }, [error, isLoading, vesselCallId]);

  const operationsDisputesRows = useMemo(() => {
    const disputes = disputesData?.items ?? [];

    return disputes.map((dispute) => mapDisputeToRow(dispute, t));
  }, [disputesData?.items, t]);

  const dataAvailable = operationsDisputesRows.length > 0;
  const totalDisputesCount = disputesData?.totalElements ?? operationsDisputesRows.length;

  const handleOpenDisputeDetails = (row: DisputeRow) => {
    setSelectedDisputeId(row.id);
    setIsDisputeDetailsDrawerOpen(true);
  };

  const handleCloseDisputeDetails = () => {
    setIsDisputeDetailsDrawerOpen(false);
    setSelectedDisputeId(null);
  };

  const handleOpenReviewResolution = (mode: ReviewResolutionMode) => {
    setIsDisputeDetailsDrawerOpen(false);
    setReviewResolutionMode(mode);
  };

  const handleCloseReviewResolution = () => {
    setReviewResolutionMode(null);
    if (selectedDisputeId) {
      setIsDisputeDetailsDrawerOpen(true);
    }
  };

  const filterFields: Record<string, FilterFieldDefinition> = useMemo(
    () =>
      getDisputeFilterFields(
        t,
        fromToLocations
          .filter(
            (location) =>
              location.location === "BERTH" &&
              Boolean(location.locationName) &&
              Boolean(location.fromToLocationCode),
          )
          .map(({ fromToLocationCode, locationName }) => ({
            value: fromToLocationCode,
            label: locationName,
          }))
          .filter(
            (option, index, options) =>
              options.findIndex(({ value }) => value === option.value) === index,
          ),
      ),
    [fromToLocations, t],
  );

  const onApplyFilters = (nextFilters: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(nextFilters);
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(nextFilters));
  };

  const onClearFilters = () => {
    if (filters.length > 0) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    setPage(1);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };

  const headers: Header[] = useMemo(
    () => [
      {
        field: "disputeRequestNumber",
        columnName: t(`${LANG_BASE}.grid.id`),
        flex: 0.2,
      },
      {
        field: "terminal",
        columnName: t(`${LANG_BASE}.grid.terminal`),
        minWidth: 170,
        flex: 0.8,
      },
      {
        field: "associatedMovement",
        columnName: t(`${LANG_BASE}.grid.associated-movement`),
        minWidth: 220,
        flex: 1.2,
      },
      {
        field: "disputeReason",
        columnName: t(`${LANG_BASE}.grid.dispute-reason`),
        minWidth: 280,
        flex: 2,
      },
      {
        field: "submittedBy",
        columnName: t(`${LANG_BASE}.grid.submitted-by`),
        minWidth: 170,
      },
      {
        field: "submissionDate",
        columnName: t(`${LANG_BASE}.grid.submission-date`),
        type: "dateTime",
        minWidth: 170,
      },
      {
        field: "disputeStatus",
        columnName: t(`${LANG_BASE}.grid.status`),
        minWidth: 180,
        flex: 1,
      },
    ],
    [t],
  );

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  const isEmptyState = (!dataAvailable && filters.length === 0) || isError;

  return (
    <>
      {isEmptyState ? (
        <div className="flex flex-col items-center justify-center gap-5 pt-6 pb-6 mb-6">
          <NoTableEntries
            noMargin
            dataName={""}
            actionName={""}
            customMessage={`${LANG_BASE}.empty-disputes`}
            descriptionComponents={{ strong: <strong /> }}
          />
          <OperationsDisputesFlowButtons
            onDispute={() => setIsDisputeDrawerOpen(true)}
            onNoDispute={() => setIsNoDisputeDrawerOpen(true)}
          />
        </div>
      ) : (
        <div className="w-full mb-6">
          <div className="mb-4 flex justify-end">
            <OperationsDisputesFlowButtons
              onDispute={() => setIsDisputeDrawerOpen(true)}
              onNoDispute={() => setIsNoDisputeDrawerOpen(true)}
            />
          </div>
          <CustomDataTable<DisputeRow>
            title=""
            actionName=""
            rows={operationsDisputesRows}
            headers={headers}
            testId="operations-disputes-grid"
            page={page}
            pageSize={pageSize}
            totalCount={totalDisputesCount}
            onRowClick={handleOpenDisputeDetails}
            onPageChange={(newPage, newPageSize) => {
              if (newPage !== page || newPageSize !== pageSize) {
                startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
              }
              setPage(newPage);
              setPageSize(newPageSize);
            }}
            navigationLinkBase=""
            filters={filters}
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
            availableFields={filterFields}
            displaySubTitle
            shouldFreezeExtraColumn
            actionColumn={{
              width: 160,
              actionTitle: t(`${LANG_BASE}.grid.view`),
              onActionClick: handleOpenDisputeDetails,
            }}
            pageSizeOptions={[25, 50, 100]}
          />
          <DisputeDetailsDrawer
            disputeId={selectedDisputeId}
            isOpen={isDisputeDetailsDrawerOpen}
            isCurrentLocationFOAA={isCurrentLocationFOAA}
            onClose={handleCloseDisputeDetails}
            onChangeToNoDispute={() => {
              setIsDisputeDetailsDrawerOpen(false);
              setIsNoDisputeDrawerOpen(true);
            }}
            onUpdateDispute={() => {
              setIsUpdateDisputeDrawerOpen(true);
            }}
            onResolveDispute={() => {
              setIsResolveDisputeDrawerOpen(true);
            }}
            onAcceptResolution={() => handleOpenReviewResolution("accept")}
            onRejectResolution={() => handleOpenReviewResolution("reject")}
          />
        </div>
      )}
      <DisputeDrawer
        mode={
          isUpdateDisputeDrawerOpen
            ? "update-dispute"
            : isNoDisputeDrawerOpen
              ? "no-dispute"
              : "dispute"
        }
        disputeId={selectedDisputeId}
        isOpen={isNoDisputeDrawerOpen || isDisputeDrawerOpen || isUpdateDisputeDrawerOpen}
        onClose={() => {
          const isChangingExistingDispute = isNoDisputeDrawerOpen && Boolean(selectedDisputeId);

          setIsNoDisputeDrawerOpen(false);
          setIsDisputeDrawerOpen(false);
          setIsUpdateDisputeDrawerOpen(false);
          setIsResolveDisputeDrawerOpen(false);
          setReviewResolutionMode(null);

          if (isChangingExistingDispute) {
            setIsDisputeDetailsDrawerOpen(false);
            setSelectedDisputeId(null);
          }
        }}
      />
      <ResolveDisputeDrawer
        disputeId={selectedDisputeId}
        isOpen={isResolveDisputeDrawerOpen}
        onClose={() => setIsResolveDisputeDrawerOpen(false)}
      />
      <ReviewResolutionDrawer
        disputeId={selectedDisputeId}
        isOpen={reviewResolutionMode !== null}
        mode={reviewResolutionMode ?? "accept"}
        onClose={handleCloseReviewResolution}
      />
    </>
  );
};

export default OperationsDisputes;
